/**
 * HeroKPIRow — 5 KPI cards at top of dashboard (HomePage-style centered cards with shadow).
 */
import React, {useState, useEffect, useRef} from 'react'
import {Box, Typography} from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import {KPI_ACCENTS, FONT, WEIGHT, BORDER, SUBTLE, WHITE, SHADOW} from './dashboardStyles'

const EMPTY_KPI = {totalShipments: 0, pendingPickups: 0, inTransit: 0, ndrPending: 0, delivered: 0, rto: 0, stuckNonAbd: 0, breached: 0}

function useCountUp(end, duration = 1200) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  useEffect(() => {
    const target = Number(end) || 0
    let start = null
    const step = (ts) => {
      if (!start) start = ts
      const p = Math.min((ts - start) / duration, 1)
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target))
      if (p < 1) ref.current = requestAnimationFrame(step)
    }
    ref.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(ref.current)
  }, [end, duration])
  return count
}

const CARD_TOTAL = {key: 'totalShipments', label: 'Total Shipments', icon: <LocalShippingOutlinedIcon />, accent: KPI_ACCENTS.totalShipments}
const CARD_PENDING = {key: 'pendingPickups', label: 'Pending Pickups', icon: <PendingActionsOutlinedIcon />, accent: KPI_ACCENTS.pendingPickups}
const CARD_TRANSIT = {key: 'inTransit', label: 'In Transit', icon: <FlightTakeoffOutlinedIcon />, accent: KPI_ACCENTS.inTransit}
const CARD_NDR = {key: 'ndrPending', label: 'NDR Pending', icon: <ReportProblemOutlinedIcon />, accent: KPI_ACCENTS.ndrPending}
const CARD_RTO = {key: 'rto', label: 'RTO', icon: <ReportProblemOutlinedIcon />, accent: KPI_ACCENTS.rto}
const CARD_NPR = {key: 'nprPending', label: 'NPR Pending', icon: <ReportProblemOutlinedIcon />, accent: KPI_ACCENTS.ndrPending}
const CARD_BREACHED = {key: 'breached', label: 'Breached (Non-ABD)', icon: <ReportProblemOutlinedIcon />, accent: KPI_ACCENTS.breached}
const CARD_STUCK = {key: 'stuckNonAbd', label: 'Stuck (Non-ABD)', icon: <HourglassEmptyOutlinedIcon />, accent: KPI_ACCENTS.stuckNonAbd}
const CARD_DELIVERED = {key: 'delivered', label: 'Delivered', icon: <CheckCircleOutlineIcon />, accent: KPI_ACCENTS.delivered}

// NDR is a B2C/parcel concept — PTL freight has no NDR. The PTL row instead surfaces
// SLA-breached Non-ABD shipments (CT isBreached) plus a live "Stuck Shipments" count.
// Reverse flow uses NPR (Non-Pickup Report) instead of NDR.
// RTO is included so the D2C cards reconcile to the total (previously RTO was counted in
// kpiData but never shown, leaving a gap between the cards and the stated total).
const D2C_CONFIG = [CARD_TOTAL, CARD_PENDING, CARD_TRANSIT, CARD_NDR, CARD_RTO, CARD_DELIVERED]
const D2C_REVERSE_CONFIG = [CARD_TOTAL, CARD_PENDING, CARD_TRANSIT, CARD_NPR, CARD_RTO, CARD_DELIVERED]
const PTL_CONFIG = [CARD_TOTAL, CARD_PENDING, CARD_TRANSIT, CARD_BREACHED, CARD_STUCK, CARD_DELIVERED]

// Always mounted (stable hook order) so a loading/refetch toggle never remounts the
// component and resets the count-up animation to 0. While loading we show a dim placeholder.
const numSx = (color) => ({fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color, fontVariantNumeric: 'tabular-nums', mt: 0.75})
function AnimatedKPI({value, color, loading}) {
  const animated = useCountUp(loading ? 0 : value)
  if (loading) return <Typography sx={{...numSx(color), opacity: 0.35}}>—</Typography>
  return <Typography sx={numSx(color)}>{animated.toLocaleString()}</Typography>
}

function HeroKPIRow({data, loading, vertical, flow = 'ALL'}) {
  const displayData = data || EMPTY_KPI
  const config = vertical === 'B2B' ? PTL_CONFIG : (flow === 'REVERSE' ? D2C_REVERSE_CONFIG : D2C_CONFIG)

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: `repeat(${config.length},1fr)`},
      bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden',
      boxShadow: SHADOW.card, mb: '20px',
    }}>
      {config.map(({key, label, accent}) => (
        <Box key={key} sx={{p: 1.75, borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box sx={{width: 7, height: 7, borderRadius: '2px', bgcolor: accent, flex: 'none'}} />
            <Typography sx={{fontFamily: FONT, fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE}}>{label}</Typography>
          </Box>
          <AnimatedKPI value={displayData[key]} color={accent} loading={loading} />
        </Box>
      ))}
    </Box>
  )
}

export default React.memo(HeroKPIRow)
